import React, { Component, Fragment } from "react";

import PropTypes from "prop-types";

import { ADDRESS, ADDRESS_FIELDS } from "constants/address";
import { DEFAULT_LOCATOR_STRATEGY } from "constants/locatorStrategy";
import { COUNTRY_CODES, MARKET_CENTER } from "constants/market";
import { SERVICE_METHOD, SERVICE_METHOD_SORT } from "constants/order";

import "components/StoreLocator/Market/MX.css";
import GoogleMapsTypeAhead from "components/StoreLocator/Strategy/GoogleMapsTypeAhead";
import Input from "components/StoreLocator/Strategy/Input";
import PinDrop, { MAP_OPTIONS } from "components/StoreLocator/Strategy/PinDrop";
import Select from "components/StoreLocator/Strategy/Select";

const MARKET_CODE = COUNTRY_CODES.MEXICO;

const SHOW_ADDRESS_FIELDS_1 = Object.freeze({
  fields: [
    ADDRESS_FIELDS[ADDRESS.POSTAL_CODE],
    ADDRESS_FIELDS[ADDRESS.REGION_CODE],
    ADDRESS_FIELDS[ADDRESS.CITY],
  ],
});

const SHOW_ADDRESS_FIELDS_2 = Object.freeze({
  fields: [
    ADDRESS_FIELDS[ADDRESS.STREET_NAME],
    ADDRESS_FIELDS[ADDRESS.STREET_NUMBER],
    ADDRESS_FIELDS[ADDRESS.UNIT_NUMBER],
  ],
});

const optionalFields = [ADDRESS.UNIT_NUMBER];

const NEIGHBORHOOD_PLACEHOLDER = "selectneighborhood";

const requiredInputFields = [
  ...SHOW_ADDRESS_FIELDS_1.fields,
  ...SHOW_ADDRESS_FIELDS_2.fields,
]
  .map(({ mapping }) => mapping)
  .filter((field) => !optionalFields.includes(field));

const requiredFields = [...requiredInputFields, ADDRESS.NEIGHBORHOOD];

const placesMethods = Object.freeze([SERVICE_METHOD.CARRYOUT]);

const MARKET_ZOOM = 6;

class MX extends Component {
  constructor(props) {
    super(props);

    this.map = React.createRef();

    this.state = {
      invalid: [],
    };

    this.handleChange = this.handleChange.bind(this);
    this.handleClear = this.handleClear.bind(this);
    this.handleCepUpdate = this.handleCepUpdate.bind(this);
    this.handleFocus = this.handleFocus.bind(this);
    this.handleNeighborhoodChange = this.handleNeighborhoodChange.bind(this);
    this.handlePinDrop = this.handlePinDrop.bind(this);
    this.handleSearch = this.handleSearch.bind(this);
    this.setDefaultLocatorStrategy = this.setDefaultLocatorStrategy.bind(this);
    this.validateInputs = this.validateInputs.bind(this);
    this.handlePlaceChange = this.handlePlaceChange.bind(this);
  }

  componentDidMount() {
    const { maps, serviceMethod, setUsesPlaces } = this.props;
    this.geocoderService = new maps.Geocoder();
    this.geocoderOK = maps.GeocoderStatus.OK;

    placesMethods.includes(serviceMethod) && setUsesPlaces(true);
  }

  componentDidUpdate(prevProps) {
    if (this.props.searchCep !== prevProps.searchCep) {
      if (this.props.searchCep.length === 0) {
        this.handleCepUpdate({
          regionCode: "",
          postalCode: "",
          city: "",
          neighborhood: "",
        });
      } else {
        const [firstCep = {}] = this.props.searchCep;
        const { neighborhood } = this.props;
        // keep the Google/agent selected neighborhood when the new CEP results still offer it
        const keepNeighborhood = this.props.searchCep.some(
          (cep) => cep?.neighborhood === neighborhood
        );

        const searchCepMapper = {
          regionCode: firstCep.state,
          postalCode: firstCep.cep,
          city: firstCep.city,
          neighborhood: keepNeighborhood ? neighborhood : firstCep.neighborhood,
        };

        this.handleCepUpdate(searchCepMapper);
      }
    }
  }

  validateInputs() {
    const cepValues = this.props.searchCep[0] || {};
    const fieldValues = {
      ...this.props,
      regionCode: cepValues.state,
      postalCode: cepValues.cep,
      city: cepValues.city,
    };
    const invalid = requiredFields.filter(
      (field) => !fieldValues[field]?.length
    );

    this.setState({
      invalid,
    });

    return invalid.length === 0;
  }

  handleFocus(update) {
    const [fieldName] = Object.keys(update);

    this.setState({
      invalid: this.state.invalid.filter(
        (invalidFieldName) => fieldName !== invalidFieldName
      ),
    });
  }

  setDefaultLocatorStrategy() {
    this.handleClear();
    this.props.setLocatorStrategy(DEFAULT_LOCATOR_STRATEGY);
  }

  handleChange(updates) {
    this.setState({ ...updates });
  }

  handleNeighborhoodChange(selectedValue) {
    const neighborhood =
      selectedValue === NEIGHBORHOOD_PLACEHOLDER ? "" : selectedValue;
    const invalid = this.state.invalid.filter(
      (field) => field !== ADDRESS.NEIGHBORHOOD
    );

    this.setState({
      invalid: neighborhood ? invalid : [...invalid, ADDRESS.NEIGHBORHOOD],
    });

    this.props.handleChange({ neighborhood });
  }

  handleSearch() {
    const { address, placeId } = this.props;

    this.getGeocodedResult(
      { ...(placeId ? { placeId } : { address }) },
      ({ geometry }) => {
        this.map.current.dropPin(geometry.location);
      },
      { updateAddress: !placeId }
    );
  }

  handleClear() {
    this.props.getSearchCep({
      cep: "",
      countryCode: MARKET_CODE,
    });
    const updates = {
      state: "",
      city: "",
      postalCode: "",
      streetName: "",
      streetNumber: "",
      neighborhood: "",
      address: "",
      placeId: "",
    };

    this.props.handleChange(updates);
  }

  transformAddress(address) {
    const newAddress = {};
    address.forEach((component) => {
      const { types, long_name, short_name } = component;
      types.forEach((type) => {
        if (!newAddress[type]) {
          newAddress[type] = {};
        }
        newAddress[type] = {
          long_name,
          short_name,
        };
      });
    });

    return newAddress;
  }

  getGeocodedResult(request, callback) {
    this.geocoderService.geocode(request, (response, status) => {
      if (status !== this.geocoderOK) {
        this.props.onError(status);
        return;
      }

      const {
        address_components,
        geometry,
        place_id,
        formatted_address: address,
      } = response[0];

      callback({
        address,
        addressComponents: address_components,
        geometry,
        placeId: place_id,
      });
    });
  }

  mapAddressComponentsToQuery(addressComponents, query = {}) {
    return addressComponents.reduce((all) => {
      const formattedAddress = this.transformAddress(addressComponents);
      const { route, sublocality, street_number } = formattedAddress;

      return Object.assign(all, {
        neighborhood: sublocality?.short_name,
        streetNumber: street_number?.short_name,
        streetName: route?.short_name,
      });
    }, query);
  }

  handlePinDrop(latLng) {
    this.handleClear();
    this.getGeocodedResult(
      { location: latLng },
      ({ addressComponents, placeId, geometry, address }) => {
        const { postal_code } = this.transformAddress(addressComponents);

        const updateObject = Object.assign(
          {},
          ...Object.keys(ADDRESS_FIELDS)
            .filter((field) => field !== ADDRESS.ADDRESS_TYPE)
            .map((field) => ({ [field]: "" })),
          this.mapAddressComponentsToQuery(addressComponents, placeId),
          {
            latitude: geometry.location.lat(),
            longitude: geometry.location.lng(),
          },
          {
            address,
            placeId,
          }
        );

        this.props.handleChange(updateObject, {
          props: {
            onUpdate: () => {
              if (postal_code?.short_name) {
                this.props.getSearchCep({
                  cep: postal_code.short_name,
                  countryCode: MARKET_CODE,
                });
              }
            },
          },
        });
      }
    );
  }

  handleCepUpdate(newCep = {}) {
    this.props.handleChange(newCep);
  }

  handlePlaceChange({ address: addressValue, placeId }) {
    this.props.handleChange({
      address: addressValue,
      placeId,
    });

    if (placeId) {
      this.getGeocodedResult(
        { placeId },
        ({ geometry }) => {
          this.map.current.dropPin(geometry.location);
        },
        { updateAddress: false }
      );
    }
  }

  render() {
    const {
      address,
      deliveryInstructions,
      searchCep,
      handleChange,
      maps,
      marketConfigs: { DELIVERY_INSTRUCTIONS_MAX_LENGTH },
      neighborhood,
      serviceMethod,
      t,
      locatorStrategy,
    } = this.props;
    const isDefaultLocatorStrategy =
      locatorStrategy === DEFAULT_LOCATOR_STRATEGY;

    const updatedNeighborhood =
      searchCep?.length > 0
        ? searchCep.reduce((combinedResult, cepResult) => {
            combinedResult[cepResult.neighborhood] = cepResult.neighborhood;
            return combinedResult;
          }, {})
        : {};

    const { handleFocus, validateInputs } = this;
    const { invalid } = this.state;

    const searchCepMapper = {
      regionCode: searchCep[0]?.state,
      postalCode: searchCep[0]?.cep,
      city: searchCep[0]?.city,
      neighborhood: searchCep[0]?.neighborhood,
    };

    const countryCodeList = window.ENVCONFIG.includeUSMarketForTestStore
      ? [MARKET_CODE, COUNTRY_CODES.UNITED_STATES]
      : [MARKET_CODE];

    switch (serviceMethod) {
      case SERVICE_METHOD.DELIVERY:
        return (
          <div className="grid">
            {isDefaultLocatorStrategy && (
              <div className="grid__cell--1 grid__cell--1/2@desktop margin__bottom--1_25rem">
                <PinDrop
                  mapOptions={Object.assign({}, MAP_OPTIONS, {
                    mapTypeControl: true,
                  })}
                  mapCenter={MARKET_CENTER.MEXICO}
                  mapZoom={MARKET_ZOOM}
                  maps={maps}
                  onChange={this.handlePinDrop}
                  ref={this.map}
                />
              </div>
            )}
            <div className="grid__cell--1 grid__cell--1/2@desktop">
              <div className="grid">
                {isDefaultLocatorStrategy && (
                  <Fragment>
                    <GoogleMapsTypeAhead
                      className="grid__cell--1"
                      input={address}
                      label={t(ADDRESS_FIELDS[ADDRESS.ADDRESS].label)}
                      maps={maps}
                      name="address"
                      onChange={this.handlePlaceChange}
                      options={{
                        componentRestrictions: {
                          country: countryCodeList,
                        },
                        types: ["geocode"],
                      }}
                      quidBase="store-locator-address"
                      required
                    />

                    {SHOW_ADDRESS_FIELDS_1.fields.map(
                      ({ label, mapping: key }) => (
                        <Input
                          className="grid__cell--1 grid__cell--1/2@desktop"
                          handleBlur={validateInputs}
                          handleChange={handleChange}
                          handleFocus={handleFocus}
                          isInvalid={
                            invalid.includes(key) &&
                            !searchCepMapper[key]?.length
                          }
                          isRequired={!optionalFields.includes(key)}
                          key={key}
                          label={t(label)}
                          name={key}
                          quidBase="store-locator-address-field"
                          showReadOnlyError={true}
                          t={t}
                          value={searchCepMapper[key]}
                          readOnly={true}
                        />
                      )
                    )}

                    <Select
                      className="grid__cell--1 grid__cell--1/2@desktop"
                      handleChange={this.handleNeighborhoodChange}
                      isInvalid={
                        invalid.includes(ADDRESS.NEIGHBORHOOD) &&
                        !neighborhood?.length
                      }
                      label={t("customer:locations.address.neighborhood")}
                      name="neighborhood"
                      quidBase="store-locator-neighborhood"
                      source={Object.freeze({
                        [NEIGHBORHOOD_PLACEHOLDER]: t(
                          "customer:locations.address.select_neighborhood"
                        ),
                        ...updatedNeighborhood,
                      })}
                      t={t}
                      value={neighborhood || NEIGHBORHOOD_PLACEHOLDER}
                      required={true}
                      translations={false}
                    />

                    {SHOW_ADDRESS_FIELDS_2.fields.map(
                      ({ label, mapping: key }) => (
                        <Input
                          className="grid__cell--1 grid__cell--1/2@desktop"
                          handleBlur={validateInputs}
                          handleChange={handleChange}
                          handleFocus={handleFocus}
                          isInvalid={
                            invalid.includes(key) && !this.props[key]?.length
                          }
                          isRequired={!optionalFields.includes(key)}
                          key={key}
                          label={t(label)}
                          name={key}
                          quidBase="store-locator-address-field"
                          t={t}
                          value={this.props[key]}
                        />
                      )
                    )}
                  </Fragment>
                )}

                <Input
                  className="grid__cell--1"
                  handleChange={handleChange}
                  label={t(ADDRESS_FIELDS[ADDRESS.DELIVERY_INSTRUCTIONS].label)}
                  name="deliveryInstructions"
                  quidBase="store-locator-address-field"
                  value={deliveryInstructions}
                  maxLength={DELIVERY_INSTRUCTIONS_MAX_LENGTH}
                  t={t}
                />

                <div className="grid__cell--1">
                  <div className="grid form-controls">
                    <button
                      className="btn grid__cell--1/2 grid__cell--1/4@widescreen"
                      data-icon="clear"
                      data-quid="store-locator-clear"
                      onClick={this.handleClear}
                      type="button"
                    >
                      {t("shared:action.clear_form")}
                    </button>
                    {isDefaultLocatorStrategy && (
                      <button
                        className="btn grid__cell--1/2 grid__cell--1/4@widescreen"
                        data-icon="pin_drop"
                        data-quid="store-locator-search"
                        onClick={this.handleSearch}
                        type="button"
                      >
                        {t("shared:action.drop_pin")}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      case SERVICE_METHOD.CARRYOUT:
      default:
        return (
          <GoogleMapsTypeAhead
            className="grid__cell--1"
            input={address}
            label={t(ADDRESS_FIELDS[ADDRESS.ADDRESS].label)}
            maps={maps}
            name="address"
            onChange={({ address: addressValue, placeId }) =>
              handleChange({
                address: addressValue,
                placeId,
              })
            }
            options={{
              componentRestrictions: {
                country: countryCodeList,
              },
              types: ["geocode"],
            }}
            quidBase="store-locator-address"
          />
        );
    }
  }
}

MX.propTypes = {
  address: PropTypes.string,
  deliveryInstructions: PropTypes.string,
  getRegions: PropTypes.func.isRequired,
  getSearchCep: PropTypes.func.isRequired,
  handleChange: PropTypes.func.isRequired,
  locatorStrategy: PropTypes.string.isRequired,
  maps: PropTypes.objectOf(PropTypes.any).isRequired,
  marketConfigs: PropTypes.shape({
    DELIVERY_INSTRUCTIONS_MAX_LENGTH: PropTypes.number,
  }).isRequired,
  neighborhood: PropTypes.string,
  onError: PropTypes.func,
  placeId: PropTypes.string,
  searchCep: PropTypes.arrayOf(PropTypes.object).isRequired,
  serviceMethod: PropTypes.oneOf(SERVICE_METHOD_SORT),
  setLocatorStrategy: PropTypes.func.isRequired,
  setUsesPlaces: PropTypes.func.isRequired,
  unitNumber: PropTypes.string,
};

MX.defaultProps = {
  address: "",
  addressType: ADDRESS.LANDED,
  deliveryInstructions: "",
  getSearchCep: () => {},
  neighborhood: "",
  onError: (error) => {
    //eslint-disable-next-line no-console
    window.ENVCONFIG.logErrors && console.error(error);
  },
  searchCep: {},
  serviceMethod: SERVICE_METHOD.CARRYOUT,
  unitNumber: "",
};

export default MX;
